import { useState, useEffect } from 'react';
import { User, UserRole, ROLE_HIERARCHY } from '@/types/index';
import { authService } from '@services/auth-service';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser && authService.isAuthenticated()) {
        try {
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);

          // Recharger le profil depuis l'API pour avoir les données à jour (firstName/lastName)
          try {
            const freshProfile = await authService.getProfile();
            setUser(freshProfile);
            localStorage.setItem('user', JSON.stringify(freshProfile));
          } catch (error) {
            // Si l'API échoue, garder les données du localStorage
            console.error('Erreur lors du rechargement du profil:', error);
          }
        } catch (error) {
          localStorage.removeItem('user');
          authService.logout();
        }
      }
      setLoading(false);
    };

    initializeAuth();

    // Écouter les changements d'utilisateur
    const handleUserUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      setUser(customEvent.detail);
      setIsAuthenticated(true);
    };

    window.addEventListener('user-updated', handleUserUpdate);

    // Cleanup
    return () => {
      window.removeEventListener('user-updated', handleUserUpdate);
    };
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authService.login(email, password);
    authService.setToken(response.token);
    setUser(response as any);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(response));
    window.dispatchEvent(new CustomEvent('user-updated', { detail: response }));
    return response;
  };

  const register = async (email: string, password: string, name?: string) => {
    const response = await authService.register(email, password, name);
    authService.setToken(response.token);
    setUser(response as any);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(response));
    return response;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = '/login';
  };

  /** Retourne true si l'utilisateur a au moins le rôle `minRole` */
  const hasMinRole = (minRole: UserRole): boolean => {
    const userLevel = ROLE_HIERARCHY[user?.role ?? 'FREEMIUM'] ?? 0;
    const minLevel = ROLE_HIERARCHY[minRole] ?? 0;
    return userLevel >= minLevel;
  };

  return { user, loading, isAuthenticated, login, register, logout, hasMinRole };
};
